/**
 * 主站与若依同源集成：静默登录若依并写入 Cookie `Admin-Token`（与 frontend/RuoYi-Vue3/src/utils/auth.js 一致）。
 * 不经过 apiClient，避免附带主站 JWT。
 */
import axios from 'axios'

const RUOYI_DEV_API_PREFIX = '/admin/dev-api'
export const RUOYI_ADMIN_TOKEN_KEY = 'Admin-Token'

const ruoyiAxios = axios.create({
  baseURL: '',
  timeout: 20000,
  headers: {
    'Content-Type': 'application/json;charset=utf-8',
  },
  validateStatus: (s) => s >= 200 && s < 300,
})

function setCookie(name: string, value: string, path = '/') {
  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; path=${path}`
}

function deleteCookie(name: string, path = '/') {
  document.cookie = `${encodeURIComponent(name)}=; path=${path}; max-age=0`
}

/** 写入若依前端使用的 Admin-Token（会话级，与若依 Cookies.set 默认行为接近） */
export function setRuoYiAdminTokenCookie(token: string) {
  setCookie(RUOYI_ADMIN_TOKEN_KEY, token, '/')
}

/** 主站登出时清除若依会话 Cookie */
export function removeRuoYiAdminTokenCookie() {
  deleteCookie(RUOYI_ADMIN_TOKEN_KEY, '/')
}

export type RuoyiSilentLoginResult =
  | { ok: true }
  | { ok: false; reason: 'captcha_required' | 'network' | 'bad_credentials' | 'unexpected' }

/** 是否与 Profile 中「管理员」入口一致（需同步若依） */
export function shouldSyncRuoYiForAdmin(roles: string[] | undefined, role?: string): boolean {
  const set = new Set((roles ?? []).map((r) => String(r).toUpperCase()))
  if (set.has('ADMIN')) return true
  if (role && String(role).toUpperCase() === 'ADMIN') return true
  return false
}

interface AjaxLike {
  code?: number
  msg?: string
  token?: string
  captchaEnabled?: boolean
}

/**
 * 主站已验证用户名密码后调用：探测验证码开关，关闭时再调用若依 /login 并写入 Cookie。
 */
export async function ruoyiSilentLogin(
  username: string,
  password: string
): Promise<RuoyiSilentLoginResult> {
  try {
    const cap = await ruoyiAxios.get<AjaxLike>(`${RUOYI_DEV_API_PREFIX}/captchaImage`)
    const capData = cap.data as AjaxLike & Record<string, unknown>
    if (capData.code !== undefined && capData.code !== 200) {
      return { ok: false, reason: 'unexpected' }
    }
    // 与若依 login.vue：captchaEnabled 缺省时视为开启
    const captchaOn = capData.captchaEnabled !== false
    if (captchaOn) {
      return { ok: false, reason: 'captcha_required' }
    }

    const loginRes = await ruoyiAxios.post<AjaxLike>(`${RUOYI_DEV_API_PREFIX}/login`, {
      username,
      password,
      code: '',
      uuid: '',
    })

    const body = loginRes.data as AjaxLike
    if (body.code !== undefined && body.code !== 200) {
      if (body.code === 401 || body.code === 500) {
        return { ok: false, reason: 'bad_credentials' }
      }
      return { ok: false, reason: 'unexpected' }
    }

    const token = body.token
    if (!token || typeof token !== 'string') {
      return { ok: false, reason: 'unexpected' }
    }

    setRuoYiAdminTokenCookie(token)
    return { ok: true }
  } catch (e) {
    console.warn('[ruoyiSso] 静默登录异常:', e)
    return { ok: false, reason: 'network' }
  }
}
